import { Card, Flex, Heading, Text } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { FC, ReactNode, useMemo } from "react";

type StatCardTone = "default" | "success" | "warning" | "danger";

const toneToColor: Record<StatCardTone, string> = {
  default: "var(--gray-12, #0f172a)",
  success: "var(--green-11, #18794e)",
  warning: "var(--amber-11, #ad5f00)",
  danger: "var(--red-11, #d92d20)",
};

const StatCardWrapper = styled.div`
  border: 1px solid var(--gray-4, #d0d5dd);
  border-radius: 16px;
  background: var(--gray-1, #ffffff);
`;

const StatCardContainer = styled(Card)`
  border: none;
  background: transparent;
  box-shadow: none;
  border-radius: inherit;
`;

const StatValue = styled(Heading)<{ $tone: StatCardTone }>`
  color: ${({ $tone }) => toneToColor[$tone]};
`;

export interface StatCardProps {
  label: string;
  value: number | string;
  tone?: StatCardTone;
  description?: string;
  icon?: ReactNode;
  progressPercent?: number;
  progressLabel?: string;
}

export const StatCard: FC<StatCardProps> = ({
  label,
  value,
  tone = "default",
  description,
  icon,
  progressPercent,
  progressLabel,
}) => {
  const normalizedProgress = useMemo(() => {
    if (progressPercent === undefined || Number.isNaN(progressPercent)) {
      return undefined;
    }

    const clamped = Math.max(0, Math.min(100, Math.round(progressPercent)));
    const label = progressLabel ?? `${clamped}% done`;

    return { percent: clamped, label };
  }, [progressPercent, progressLabel]);

  return (
    <StatCardWrapper>
      <StatCardContainer size="2">
        <Flex direction="column" gap="2">
          <Flex align="center" justify="between" gap="3">
            <Text size="2" weight="medium" color="gray">
              {label}
            </Text>
            {icon}
          </Flex>
          <StatValue size="6" weight="bold" $tone={tone}>
            {value}
          </StatValue>
          {description ? (
            <Text size="1" color="gray">
              {description}
            </Text>
          ) : null}
          {normalizedProgress ? (
            <ProgressWrapper>
              <Text size="1" color="gray">
                {normalizedProgress.label}
              </Text>
              <ProgressTrack
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={normalizedProgress.percent}
              >
                <ProgressFill $tone={tone} style={{ width: `${normalizedProgress.percent}%` }} />
              </ProgressTrack>
            </ProgressWrapper>
          ) : null}
        </Flex>
      </StatCardContainer>
    </StatCardWrapper>
  );
};

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 6px;
  width: 100%;
  border-radius: 999px;
  background: var(--gray-4, #d0d5dd);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $tone: StatCardTone }>`
  height: 100%;
  border-radius: inherit;
  background: ${({ $tone }) => toneToColor[$tone]};
  transition: width 0.3s ease;
`;
